import React, { useState, useEffect, useCallback } from 'react';
import { List, Card, Button, Typography, Space, Input, Spin, message } from 'antd';
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { http } from '../utils/http';

const { Title, Text } = Typography;

const OutboundList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [current, setCurrent] = useState(1);
  const [pageSize] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized) {
      loadData(true);
      setInitialized(true);
    }
  }, [initialized]);

  const loadData = async (isRefresh = false) => {
    if (isRefresh) {
      setLoading(true);
      setCurrent(1);
      setDataSource([]);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = {
        page: isRefresh ? 1 : current,
        pageSize: pageSize,
        SearchWaitOutstorageOrder: true
      };

      if (keyword.trim()) {
        params.keyword = keyword.trim();
      }

      const response = await http.get('/erp/api/OutstorageOrder/GetMobileOutOrderPageList', {
        params
      });

      if (response.code === 1) {
        const newData = response.data.data || [];
        
        if (isRefresh) {
          setDataSource(newData);
        } else {
          setDataSource(prev => [...prev, ...newData]);
        }

        // 如果返回的数据少于pageSize，说明没有更多数据了
        if (newData.length < pageSize) {
          setHasMore(false);
        }

        if (!isRefresh) {
          setCurrent(prev => prev + 1);
        }
      } else {
        message.error(response.message || '获取数据失败');
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleItemClick = (item) => {
    navigate(`/outbound/${item.id}`);
  };

  const handleSearch = () => {
    loadData(true);
  };

  const handleKeywordChange = (e) => {
    setKeyword(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadData(false);
    }
  }, [loadingMore, hasMore, current]);

  // 滚动加载更多
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      loadMore();
    }
  }, [loadMore]);

  useEffect(() => {
    const container = document.querySelector('.page-content');
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const renderItem = (item) => (
    <List.Item
      onClick={() => handleItemClick(item)}
      style={{ 
        cursor: 'pointer',
        borderRadius: 12,
        marginBottom: 12,
        background: '#fff',
        border: '1px solid #f0f0f0'
      }}
    >
      <Card 
        size="small" 
        style={{ width: '100%', border: 'none' }}
        bodyStyle={{ padding: '16px 20px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {/* 出库单号 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                  {item.code || '-'}
                </Text>
              </div>
              
              {/* 客户名称 */}
              <div>
                <Text style={{ fontSize: 15 }}>
                  <span style={{ color: '#666' }}>客户: </span>
                  {item.customerName || '-'}
                </Text>
              </div>
              
              {/* 创建时间 */}
              <div>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  <span style={{ color: '#666' }}>创建时间: </span>
                  {formatDate(item.createdTime)}
                </Text>
              </div>
              
              {/* 客户出库单号 */}
              {item.customerOutstorageCode && (
                <div>
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    <span style={{ color: '#666' }}>客户出库单号: </span>
                    {item.customerOutstorageCode}
                  </Text>
                </div>
              )}
              
              {/* 报关单号 */}
              {item.declarationNo && (
                <div>
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    <span style={{ color: '#666' }}>报关单号: </span>
                    {item.declarationNo}
                  </Text>
                </div>
              )}
              
              {/* 核注清单编码 */}
              {item.entryNo && (
                <div>
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    <span style={{ color: '#666' }}>核注清单编码: </span>
                    {item.entryNo}
                  </Text>
                </div>
              )}
              
              {/* 瑞圜入库跟单号 */}
              {item.ryOrderNo && (
                <div>
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    <span style={{ color: '#666' }}>瑞圜入库跟单号: </span>
                    {item.ryOrderNo}
                  </Text>
                </div>
              )}
            </Space>
          </div>
        </div>
      </Card>
    </List.Item>
  );

  return (
    <div>
      {/* 页面头部 */}
      <div className="page-header">
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
            type="text"
            size="large"
          />
          <Title level={3} style={{ margin: 0 }}>
            出库列表
          </Title>
        </Space>
      </div>

      {/* 搜索框 */}
      <div style={{ padding: '0 20px 16px' }}>
        <Input.Search
          placeholder="请输入关键词搜索"
          value={keyword}
          onChange={handleKeywordChange}
          onSearch={handleSearch}
          onPressEnter={handleKeyPress}
          enterButton={<SearchOutlined />}
          size="large"
          style={{ width: '100%' }}
        />
      </div>

      {/* 页面内容 */}
      <div 
        className="page-content" 
        style={{ 
          height: 'calc(100vh - 180px)', 
          overflowY: 'auto',
          padding: '0 20px'
        }}
      >
        <List
          loading={loading}
          dataSource={dataSource}
          renderItem={renderItem}
          style={{ background: 'transparent' }}
        />
        
        {/* 加载更多指示器 */}
        {loadingMore && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Spin size="small" />
            <Text type="secondary" style={{ marginLeft: 8 }}>
              加载中...
            </Text>
          </div>
        )}
        
        {/* 没有更多数据提示 */}
        {!hasMore && dataSource.length > 0 && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Text type="secondary">没有更多数据了</Text>
          </div>
        )}
        
        {/* 空数据提示 */}
        {!loading && dataSource.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary">暂无数据</Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutboundList;